/**
 * Mini Fraud Exposure summary card for the Overview page (PR 2 of
 * dashboard standards).
 *
 * Pulls `/api/v1/fraud-hub/kpis` (already in the cache when the user
 * navigates from the Hub) and renders a compact view of the same three
 * numbers — Open Critical (large red), Open High, New Today — plus a
 * link to the Fraud Hub for full investigation. Sits in the right
 * column of Row 4 next to the efficiency trend chart.
 *
 * The KPIs hook is shared with the Fraud Hub so this is "free" cache-wise.
 */

import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useFraudHubKpis } from '@/api/hooks/useFraudHubKpis';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });

export function FraudExposureCard(): JSX.Element {
  // PR 3 (dashboard standards): useFraudHubKpis is now period-aware. The
  // open-status counts (Critical / High / Total) deliberately ignore the
  // period; only `new_today_count` is period-bound. We pass 'D' so the
  // "New Today" tile matches its label without the Overview page needing
  // to surface the period selector.
  const { data, isLoading, isError, error } = useFraudHubKpis('D');

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Fraud Exposure</CardTitle>
      </CardHeader>
      <CardContent className="flex h-[280px] flex-col">
        {isError ? (
          <div className="text-sm text-destructive">
            Failed to load fraud KPIs: {error?.message ?? 'unknown error'}
          </div>
        ) : isLoading || !data ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-1/2" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-6 w-2/3" />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Open Critical
              </div>
              <div className="text-5xl font-bold leading-none tabular-nums text-red-600 dark:text-red-400">
                {NUM.format(data.open_critical_count)}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Open High
                </div>
                <div className="text-2xl font-semibold tabular-nums">
                  {NUM.format(data.open_high_count)}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  New Today
                </div>
                <div className="text-2xl font-semibold tabular-nums">
                  {NUM.format(data.new_today_count)}
                </div>
              </div>
            </div>

            <div className="mt-auto">
              <Link
                to="/app/fraud-hub"
                className="inline-flex items-center gap-1 text-sm font-medium text-foreground hover:underline"
              >
                Investigate now
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default FraudExposureCard;
